import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { BarChart3, MapPin, Link as LinkIcon, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PATHS = ["/", "/carta", "/celia", "/horarios", "/programa", "/ubicacion", "/convivencia", "/sugerencias", "/afiliacion", "/avisos"];
const QR_ORIGINS = [
  { value: "mesa", label: "Mesas" },
  { value: "pantalla", label: "Pantallas" },
  { value: "cartel", label: "Carteles" },
];
const QR_DISPLAY_PAGES = ["/carta", "/celia"];
const QR_PAGE_LABELS = {
  "/carta": "Carta",
  "/celia": "Celia",
};

const getOriginLabel = (origin) => QR_ORIGINS.find((item) => item.value === origin)?.label ?? origin.replace("-", " ");

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [origin, setOrigin] = useState("");
  const [resetting, setResetting] = useState(false);

  const loadAnalytics = useCallback(() => {
    return api.get("/analytics").then((r) => setData(r.data));
  }, []);

  useEffect(() => {
    loadAnalytics();
    setOrigin(window.location.origin);
  }, [loadAnalytics]);

  const matrixMap = useMemo(() => {
    const m = {};
    if (!data) return m;
    for (const row of data.matrix) {
      m[`${row.zone}__${row.path}`] = row.count;
    }
    return m;
  }, [data]);

  const maxByPath = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.by_path.map((r) => r.count));
  }, [data]);

  const maxByDay = useMemo(() => {
    if (!data?.by_day?.length) return 1;
    return Math.max(1, ...data.by_day.map((r) => r.count));
  }, [data]);

  const maxByHour = useMemo(() => {
    if (!data?.by_hour?.length) return 1;
    return Math.max(1, ...data.by_hour.map((r) => r.count));
  }, [data]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("URL copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const resetAnalytics = async () => {
    const confirmed = window.confirm("Esta acción borrará todas las estadísticas registradas. No se puede deshacer.");
    if (!confirmed) return;

    const typed = window.prompt("Escribe exactamente BORRAR para confirmar.");
    if (typed !== "BORRAR") return;

    setResetting(true);
    try {
      await api.delete("/analytics/reset");
      toast.success("Estadísticas eliminadas correctamente.");
      await loadAnalytics();
    } catch {
      toast.error("No se pudieron eliminar las estadísticas.");
    } finally {
      setResetting(false);
    }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Cargando analítica…</div>;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <header>
        <h1 className="font-display font-black text-3xl">Analítica QR</h1>
        <p className="text-muted-foreground">Tráfico por ruta y por origen QR.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat label="Páginas vistas" value={data.pageviews_total ?? data.total} icon={BarChart3} testid="analytics-total" />
        <Stat label="Sesiones" value={data.sessions_total ?? 0} icon={BarChart3} accent testid="analytics-sessions" />
        <Stat label="Sesiones QR" value={data.sessions_qr ?? 0} icon={MapPin} testid="analytics-sessions-qr" />
        <Stat label="Sesiones directas" value={data.sessions_direct ?? 0} icon={LinkIcon} testid="analytics-sessions-direct" />
        <Stat label="Páginas / sesión" value={data.pages_per_session ?? 0} icon={BarChart3} testid="analytics-pages-session" />
        <Stat label="Hora punta" value={data.peak_hour?.count ? data.peak_hour.label : "—"} icon={BarChart3} testid="analytics-peak-hour" />
      </div>

      {/* Páginas más abiertas */}
      <section className="bg-white rounded-2xl border border-border p-5">
        <h2 className="font-display font-bold mb-3">Páginas más abiertas</h2>
        {data.by_path.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay visitas registradas.</p>
        ) : (
          <ul className="space-y-2" data-testid="analytics-by-path">
            {data.by_path.map((r) => (
              <li key={r.path} className="flex items-center gap-3">
                <div className="w-32 text-sm font-medium shrink-0">{r.label}</div>
                <div className="flex-1 h-3 bg-accent rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${(r.count / maxByPath) * 100}%` }}
                  />
                </div>
                <div className="w-10 text-right text-sm font-bold tabular-nums">{r.count}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-border p-5">
        <h2 className="font-display font-bold mb-3">Páginas vistas por día</h2>
        {!data.by_day?.length ? (
          <p className="text-sm text-muted-foreground">Aún no hay visitas registradas por día.</p>
        ) : (
          <ul className="space-y-2" data-testid="analytics-by-day">
            {data.by_day.map((r) => (
              <li key={r.date} className="flex items-center gap-3">
                <div className="w-28 text-sm font-medium shrink-0">{r.date}</div>
                <div className="flex-1 h-3 bg-accent rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${(r.count / maxByDay) * 100}%` }}
                  />
                </div>
                <div className="w-10 text-right text-sm font-bold tabular-nums">{r.count}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-border p-5">
        <h2 className="font-display font-bold mb-3">Páginas vistas por hora</h2>
        {!data.by_hour?.length ? (
          <p className="text-sm text-muted-foreground">Aún no hay visitas registradas por hora.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="analytics-by-hour">
            {data.by_hour.map((r) => (
              <div key={r.hour} className="rounded-xl bg-accent px-3 py-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{r.label}</span>
                  <span className="font-bold tabular-nums">{r.count}</span>
                </div>
                <div className="mt-2 h-2 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${(r.count / maxByHour) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Distribución por origen */}
      <section className="bg-white rounded-2xl border border-border p-5">
        <h2 className="font-display font-bold mb-3">Distribución por origen</h2>
        {data.by_zone.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ningún QR con origen ha sido escaneado todavía. Copia las URLs de abajo y genera los códigos cuando esté listo el dominio final.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="analytics-by-zone">
            {data.by_zone.map((z) => (
              <div key={z.zone} className="bg-accent rounded-xl px-3 py-2 text-sm flex justify-between">
                <span>{getOriginLabel(z.zone)}</span>
                <span className="font-bold">{z.count}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Matriz origen × ruta */}
      {data.by_zone.length > 0 && (
        <section className="bg-white rounded-2xl border border-border p-5 overflow-x-auto">
          <h2 className="font-display font-bold mb-3">Origen × Ruta</h2>
          <table className="w-full text-sm" data-testid="analytics-matrix">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-2 pr-3">Origen</th>
                {PATHS.map((p) => (
                  <th key={p} className="py-2 px-2 text-center">{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {QR_ORIGINS.map(({ value, label }) => (
                <tr key={value} className="border-t border-border">
                  <td className="py-2 pr-3 font-medium">{label}</td>
                  {PATHS.map((p) => {
                    const c = matrixMap[`${value}__${p}`] || 0;
                    return (
                      <td key={p} className={`py-2 px-2 text-center tabular-nums ${c > 0 ? "font-bold" : "text-muted-foreground"}`}>
                        {c || "·"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Generador de URLs */}
      <section className="bg-white rounded-2xl border border-border p-5">
        <h2 className="font-display font-bold mb-1">URLs para generar QR</h2>
        <p className="text-sm text-muted-foreground mb-3">Copia cada URL y genera el QR con tu herramienta favorita cuando esté listo el dominio final. Al escanear, el origen se guarda automáticamente.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {QR_ORIGINS.map((originItem) =>
            QR_DISPLAY_PAGES.map((p) => {
              const url = `${origin}${p}${p.includes("?") ? "&" : "?"}qr=${originItem.value}`;
              return (
                <button
                  key={`${originItem.value}-${p}`}
                  onClick={() => copy(url)}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-accent hover:bg-primary hover:text-primary-foreground transition text-left text-xs"
                  data-testid={`qr-url-${originItem.value}-${p.replace("/", "root") || "root"}`}
                >
                  <span className="truncate">
                    <span className="font-bold mr-2">{QR_PAGE_LABELS[p] ?? p}</span>
                    <span className="opacity-70">→ {originItem.label}</span>
                  </span>
                  <Copy size={14} className="shrink-0" />
                </button>
              );
            })
          )}
        </div>
      </section>

      <section className="border border-dashed border-border rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display font-bold">Acciones secundarias</h2>
            <p className="text-sm text-muted-foreground">Uso puntual antes de publicar las estadísticas reales.</p>
          </div>
          <button
            type="button"
            onClick={resetAnalytics}
            disabled={resetting}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            data-testid="analytics-reset-btn"
          >
            <Trash2 size={16} />
            {resetting ? "Borrando..." : "Poner estadísticas a cero"}
          </button>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-primary/10 border-primary/30" : "bg-white border-border"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <Icon size={16} className={accent ? "text-primary" : "text-muted-foreground"} />
      </div>
      <div className="font-display font-black text-3xl mt-1">{value}</div>
    </div>
  );
}
